 // src/utils/pricingPresentationFirestore.js

 /**
  * PRICING PRESENTATION FIRESTORE HELPERS
  *
  * PURPOSE
  * -------
  * This module provides the app-side Firestore read/write helpers for the
  * pricing presentation/content contract.
  *
  * This is intentionally separate from:
  *   • packages/plan-catalog/index.js
  *   • BillingContext
  *   • Stripe/checkout logic
  *
  * Why:
  * - Billing/Stripe logic should remain code-owned and stable.
  * - Pricing presentation content (labels, tooltips, compare categories,
  *   card feature ordering, best-for copy, etc.) should be editable through
  *   a controlled admin UI.
  *
  * ENTERPRISE-GRADE ACCESS MODEL
  * -----------------------------
  * For phase 1, only users with role "superAppadmin" may:
  *   • initialize
  *   • write
  *   • patch
  *   • publish (later)
  *
  * Reads are also restricted the same way for now to keep rollout safe.
  *
  * DESIGN GOALS
  * ------------
  * 1. Accept a Firestore db instance as a parameter instead of hard-coding
  *    app-level imports, so this helper stays decoupled and testable.
  * 2. Normalize all reads through the presentation contract module.
  * 3. Validate writes before persisting.
  * 4. Preserve updatedAt / updatedBy metadata.
  * 5. Provide both whole-document and targeted update helpers.
  * 6. Fail loudly and clearly when access or validation fails.
  *
  * EXPECTED USAGE
  * --------------
  * import { db } from "../firebase";
  * import {
  *   getNormalizedPricingPresentationDoc,
  *   savePricingPresentationDoc,
  * } from "../utils/pricingPresentationFirestore";
  *
  * const doc = await getNormalizedPricingPresentationDoc({
  *   db,
  *   actor: { role: userProfile?.role, uid: currentUser?.uid, email: currentUser?.email },
  * });
  *
  * await savePricingPresentationDoc({
  *   db,
  *   actor: { role: userProfile?.role, uid: currentUser?.uid, email: currentUser?.email },
  *   nextDoc,
  * });
  */

 import {
   doc,
   getDoc,
   setDoc,
   updateDoc,
   runTransaction,
   serverTimestamp,
 } from "firebase/firestore";

import {
  PRICING_PRESENTATION_DOC_PATH,
  PRICING_PRESENTATION_PLAN_KEYS,
  canEditPricingPresentation,
  canReadPricingPresentation,
  createDefaultPricingPresentationDoc,
  createDefaultFeatureRecord,
  normalizePricingPresentationDoc,
  validateFeatureRecord,
  validatePricingPresentationDoc,
} from "plan-catalog";

 /* ──────────────────────────────────────────────────────────────
  * Error types
  * ─────────────────────────────────────────────────────────── */

 export class PricingPresentationAccessError extends Error {
   constructor(message, details = {}) {
     super(message);
     this.name = "PricingPresentationAccessError";
     this.details = details;
   }
 }

 export class PricingPresentationValidationError extends Error {
   constructor(message, details = {}) {
     super(message);
     this.name = "PricingPresentationValidationError";
     this.details = details;
   }
 }

 export class PricingPresentationNotFoundError extends Error {
   constructor(message, details = {}) {
     super(message);
     this.name = "PricingPresentationNotFoundError";
     this.details = details;
   }
 }

 /* ──────────────────────────────────────────────────────────────
  * Internal helpers
  * ─────────────────────────────────────────────────────────── */

 function assertDb(db) {
   if (!db) {
     throw new Error(
       "[pricingPresentationFirestore] Firestore db instance is required.",
     );
   }
 }

 function assertCanRead(actor) {
   if (!canReadPricingPresentation(actor)) {
     throw new PricingPresentationAccessError(
       "Actor is not allowed to read pricing presentation content.",
       { actor },
     );
   }
 }

 function assertCanEdit(actor) {
   if (!canEditPricingPresentation(actor)) {
     throw new PricingPresentationAccessError(
       "Actor is not allowed to edit pricing presentation content.",
       { actor },
     );
   }
 }

 function buildUpdatedBy(actor) {
   return {
     uid: actor?.uid || null,
     email: actor?.email || null,
     role:
       actor?.role ||
       actor?.userRole ||
       actor?.profile?.role ||
       actor?.claims?.role ||
       null,
   };
 }

 function getPathSegments(path) {
   const clean = String(path || "").trim().replace(/^\/+|\/+$/g, "");
   const segments = clean.split("/").filter(Boolean);

   if (segments.length !== 2) {
     throw new Error(
       `[pricingPresentationFirestore] Invalid doc path "${path}". ` +
       "Expected a two-segment Firestore document path like collection/docId.",
     );
   }

   return segments;
 }

 function getPricingPresentationDocRef(db) {
   assertDb(db);
   const [collectionName, docId] = getPathSegments(PRICING_PRESENTATION_DOC_PATH);
   return doc(db, collectionName, docId);
 }

 function validateWholeDocOrThrow(candidate) {
   const result = validatePricingPresentationDoc(candidate);

   if (!result.ok) {
     throw new PricingPresentationValidationError(
       "Pricing presentation document failed validation.",
       { errors: result.errors, candidate },
     );
   }
 }

 function validateFeatureRecordOrThrow(featureId, featureRecord) {
   const result = validateFeatureRecord(featureId, featureRecord);

   if (!result.ok) {
     throw new PricingPresentationValidationError(
       `Feature "${featureId}" failed validation.`,
       { errors: result.errors, featureId, featureRecord },
     );
   }
 }

 function clone(value) {
   return JSON.parse(JSON.stringify(value));
 }

 function stripUndefinedDeep(value) {
   if (Array.isArray(value)) {
     return value.map(stripUndefinedDeep);
   }

   if (value && typeof value === "object") {
     return Object.entries(value).reduce((acc, [key, val]) => {
       if (val === undefined) return acc;
       acc[key] = stripUndefinedDeep(val);
       return acc;
     }, {});
   }

   return value;
 }

 /* ──────────────────────────────────────────────────────────────
  * Public: document reads
  * ─────────────────────────────────────────────────────────── */

 /**
  * Read the raw Firestore pricing presentation document.
  *
  * Returns:
  * {
  *   exists: boolean,
  *   ref,
  *   data: object|null
  * }
  */
 export async function getRawPricingPresentationDoc({
   db,
   actor,
 } = {}) {
   assertDb(db);
   assertCanRead(actor);

   const ref = getPricingPresentationDocRef(db);
   const snap = await getDoc(ref);

   return {
     exists: snap.exists(),
     ref,
     data: snap.exists() ? snap.data() : null,
   };
 }

 /**
  * Read the pricing presentation document and normalize it with defaults.
  *
  * If the document does not exist:
  * - returns normalized defaults by default
  * - or throws if throwIfMissing = true
  */
 export async function getNormalizedPricingPresentationDoc({
   db,
   actor,
   throwIfMissing = false,
 } = {}) {
   const result = await getRawPricingPresentationDoc({ db, actor });

   if (!result.exists) {
     if (throwIfMissing) {
       throw new PricingPresentationNotFoundError(
         "Pricing presentation document does not exist.",
         { path: PRICING_PRESENTATION_DOC_PATH },
       );
     }

     return normalizePricingPresentationDoc(
       createDefaultPricingPresentationDoc(),
     );
   }

   return normalizePricingPresentationDoc(result.data);
 }

 /**
  * Convenience helper for "read if exists, else null" semantics.
  */
 export async function getPricingPresentationDocIfExists({
   db,
   actor,
 } = {}) {
   const result = await getRawPricingPresentationDoc({ db, actor });

   if (!result.exists) {
     return null;
   }

   return normalizePricingPresentationDoc(result.data);
 }

 /* ──────────────────────────────────────────────────────────────
  * Public: initialization
  * ─────────────────────────────────────────────────────────── */

 /**
  * Initialize the pricing presentation doc if it does not exist.
  *
  * Safe for repeated calls due to transaction guard.
  *
  * Returns:
  * {
  *   created: boolean,
  *   doc: normalizedDoc
  * }
  */
 export async function initializePricingPresentationDoc({
   db,
   actor,
   seedDoc = null,
 } = {}) {
   assertDb(db);
   assertCanEdit(actor);

   const ref = getPricingPresentationDocRef(db);
   const updatedBy = buildUpdatedBy(actor);

   const created = await runTransaction(db, async (tx) => {
     const snap = await tx.get(ref);

     if (snap.exists()) {
       return false;
     }

     const baseDoc = seedDoc
       ? normalizePricingPresentationDoc(seedDoc)
       : normalizePricingPresentationDoc(createDefaultPricingPresentationDoc());

     validateWholeDocOrThrow(baseDoc);

     tx.set(ref, {
       ...stripUndefinedDeep(clone(baseDoc)),
       updatedAt: serverTimestamp(),
       updatedBy,
     });

     return true;
   });

   const docAfter = await getNormalizedPricingPresentationDoc({
     db,
     actor,
     throwIfMissing: true,
   });

   return {
     created,
     doc: docAfter,
   };
 }

 /* ──────────────────────────────────────────────────────────────
  * Public: whole-document writes
  * ─────────────────────────────────────────────────────────── */

 /**
  * Replace the pricing presentation document with a validated, normalized doc.
  *
  * This is the safest "Save" operation for a full admin editor.
  *
  * Returns the normalized document after save.
  */
 export async function savePricingPresentationDoc({
   db,
   actor,
   nextDoc,
 } = {}) {
   assertDb(db);
   assertCanEdit(actor);

   const ref = getPricingPresentationDocRef(db);
   const updatedBy = buildUpdatedBy(actor);

   const normalized = normalizePricingPresentationDoc(nextDoc);
   validateWholeDocOrThrow(normalized);

   await setDoc(ref, {
     ...stripUndefinedDeep(clone(normalized)),
     updatedAt: serverTimestamp(),
     updatedBy,
   });

   return getNormalizedPricingPresentationDoc({
     db,
     actor,
     throwIfMissing: true,
   });
 }

 /**
  * Alias for semantics when you want the UI to feel draft-oriented.
  * For phase 1, draft save and full save are the same Firestore operation.
  */
 export async function savePricingPresentationDraft({
   db,
   actor,
   nextDoc,
 } = {}) {
   return savePricingPresentationDoc({
     db,
     actor,
     nextDoc,
   });
 }

 /* ──────────────────────────────────────────────────────────────
  * Public: targeted patch helpers
  * ─────────────────────────────────────────────────────────── */

 /**
  * Update one planContent section.
  *
  * Example:
  * updatePricingPlanContent({
  *   db,
  *   actor,
  *   planKey: "pro",
  *   patch: {
  *     subtitle: "Everything in Standard, plus:",
  *     bestFor: "Best for growing businesses",
  *     featureIntro: "Everything in Standard, plus:",
  *     cardFeatureIds: ["asset_level_utm_tracking"]
  *   }
  * })
  */
 export async function updatePricingPlanContent({
   db,
   actor,
   planKey,
   patch,
 } = {}) {
   assertDb(db);
   assertCanEdit(actor);

   const normalizedPlanKey = String(planKey || "").trim().toLowerCase();
   if (!PRICING_PRESENTATION_PLAN_KEYS.includes(normalizedPlanKey)) {
     throw new PricingPresentationValidationError(
       `Invalid planKey "${planKey}".`,
       {
         allowedPlanKeys: PRICING_PRESENTATION_PLAN_KEYS,
         planKey,
       },
     );
   }

   const current = await getNormalizedPricingPresentationDoc({
     db,
     actor,
   });

   const existing = current.planContent[normalizedPlanKey] || {
     subtitle: "",
     bestFor: "",
     featureIntro: "Included features:",
     cardFeatureIds: [],
   };

   const next = {
     ...existing,
     ...(patch || {}),
   };

   if (!Array.isArray(next.cardFeatureIds)) {
     throw new PricingPresentationValidationError(
       `planContent.${normalizedPlanKey}.cardFeatureIds must be an array.`,
       { planKey: normalizedPlanKey, next },
     );
   }

   const nextDoc = {
     ...current,
     planContent: {
       ...current.planContent,
       [normalizedPlanKey]: {
         subtitle: typeof next.subtitle === "string" ? next.subtitle : "",
         bestFor: typeof next.bestFor === "string" ? next.bestFor : "",
         featureIntro:
           typeof next.featureIntro === "string" ?
             next.featureIntro :
             "Included features:",
         cardFeatureIds: next.cardFeatureIds.filter(Boolean).map(String),
       },
     },
   };

   return savePricingPresentationDoc({
     db,
     actor,
     nextDoc,
   });
 }

 /**
  * Upsert one feature record in the registry.
  */
 export async function upsertPricingFeatureRecord({
   db,
   actor,
   featureId,
   featureRecord,
 } = {}) {
   assertDb(db);
   assertCanEdit(actor);

   const normalizedFeatureId = String(featureId || "").trim();
   if (!normalizedFeatureId) {
     throw new PricingPresentationValidationError(
       "featureId is required.",
       { featureId },
     );
   }

   const baseRecord = createDefaultFeatureRecord(normalizedFeatureId);
   const nextRecord = {
     ...baseRecord,
     ...(featureRecord || {}),
   };

   validateFeatureRecordOrThrow(normalizedFeatureId, nextRecord);

   const current = await getNormalizedPricingPresentationDoc({
     db,
     actor,
   });

   const nextDoc = {
     ...current,
     features: {
       ...current.features,
       [normalizedFeatureId]: nextRecord,
     },
   };

   return savePricingPresentationDoc({
     db,
     actor,
     nextDoc,
   });
 }

 /**
  * Remove one feature record from the registry.
  *
  * NOTE:
  * This does NOT automatically remove the ID from cardFeatureIds.
  * We intentionally keep deletion conservative.
  */
 export async function removePricingFeatureRecord({
   db,
   actor,
   featureId,
 } = {}) {
   assertDb(db);
   assertCanEdit(actor);

   const normalizedFeatureId = String(featureId || "").trim();
   if (!normalizedFeatureId) {
     throw new PricingPresentationValidationError(
       "featureId is required.",
       { featureId },
     );
   }

   const current = await getNormalizedPricingPresentationDoc({
     db,
     actor,
   });

   const nextFeatures = { ...current.features };
   delete nextFeatures[normalizedFeatureId];

   const nextDoc = {
     ...current,
     features: nextFeatures,
   };

   return savePricingPresentationDoc({
     db,
     actor,
     nextDoc,
   });
 }

 /**
  * Replace compare categories array.
  *
  * Expect array of:
  * [
  *   { key, label, order }
  * ]
  */
 export async function updatePricingCompareCategories({
   db,
   actor,
   categories,
 } = {}) {
   assertDb(db);
   assertCanEdit(actor);

   if (!Array.isArray(categories)) {
     throw new PricingPresentationValidationError(
       "categories must be an array.",
       { categories },
     );
   }

   const sanitized = categories.map((cat, index) => {
     const safe = {
       key: typeof cat?.key === "string" ? cat.key.trim() : "",
       label: typeof cat?.label === "string" ? cat.label.trim() : "",
       order: Number.isFinite(Number(cat?.order)) ? Number(cat.order) : index + 1,
     };

     if (!safe.key || !safe.label) {
       throw new PricingPresentationValidationError(
         "Each compare category must include key and label.",
         { categories },
       );
     }

     return safe;
   });

   const current = await getNormalizedPricingPresentationDoc({
     db,
     actor,
   });

   const nextDoc = {
     ...current,
     compareCategories: sanitized,
   };

   return savePricingPresentationDoc({
     db,
     actor,
     nextDoc,
   });
 }

 /* ──────────────────────────────────────────────────────────────
  * Public: lightweight metadata patch
  * ─────────────────────────────────────────────────────────── */

 /**
  * Patch the document with a partial update using updateDoc.
  *
  * This is intentionally narrower than savePricingPresentationDoc and should
  * only be used when you know the field paths are safe.
  *
  * Prefer savePricingPresentationDoc for most admin UI saves.
  */
 export async function patchPricingPresentationDoc({
   db,
   actor,
   patch,
 } = {}) {
   assertDb(db);
   assertCanEdit(actor);

   if (!patch || typeof patch !== "object" || Array.isArray(patch)) {
     throw new PricingPresentationValidationError(
       "patch must be a plain object.",
       { patch },
     );
   }

   const ref = getPricingPresentationDocRef(db);
   const updatedBy = buildUpdatedBy(actor);

   await updateDoc(ref, {
     ...patch,
     updatedAt: serverTimestamp(),
     updatedBy,
   });

   return getNormalizedPricingPresentationDoc({
     db,
     actor,
     throwIfMissing: true,
   });
 }

 /* ──────────────────────────────────────────────────────────────
  * Public: convenience export for admin screens
  * ─────────────────────────────────────────────────────────── */

 export function getPricingPresentationDocPath() {
   return PRICING_PRESENTATION_DOC_PATH;
 }

 export function getPricingPresentationDocRefForDb(db) {
   return getPricingPresentationDocRef(db);
 }

 export const PRICING_PRESENTATION_ALLOWED_EDITOR_ROLES = [
   "superAppadmin",
 ];